import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { AppDialogUiReduxObject, ConfirmationDialogButton } from '@safarilaw-webapp/shared/dialog/store-access';
import { SafariRouterReduxObject } from '@safarilaw-webapp/shared/routing-utility/store-access';
import { Observable, of } from 'rxjs';
import { filter, mergeMap, take } from 'rxjs/operators';
import { UiManagerService } from '../../services/ui-manager/ui-manager.service';

@Injectable({
  providedIn: 'root'
})
// eslint-disable-next-line @typescript-eslint/no-deprecated -- will fix later
export class PageLeaveGuard {
  #forceNavigate(nextState?: RouterStateSnapshot) {
    // If we're navigating to an error page, we don't want to check for dirty
    if (nextState && (nextState.url === '/error' || nextState.url === '/404' || nextState.url === '/403')) {
      return true;
    }
    return false;
  }
  #checkNavigateAway(component, currentState: RouterStateSnapshot, nextState?: RouterStateSnapshot) {
    // If the page is not navigating back to itself (post save) let the page know so it can do
    // cleanup if needed
    if (nextState.url != currentState.url && typeof component.onPageLeave == 'function') {
      try {
        component.onPageLeave(nextState);
      } catch (err) {
        // If something blows up in  onPageLeave while we're trying to navigate away from that page
        // weird things might happen. We'll let the original error handling go through, but then we'll setTImeout
        // this one. So the result will be that the user will see the error from onPageLeave, but in our logs we
        // should receive both in order.
        setTimeout(() => {
          throw err;
        });
      }
    }
  }
  constructor(
    private _uiManager: UiManagerService,
    private _store: Store<any>,
    private _appDialogUiReduxObject: AppDialogUiReduxObject,
    private _routerReduxObject: SafariRouterReduxObject
  ) {}
  canDeactivate(component: any, currentRoute: ActivatedRouteSnapshot, currentState: RouterStateSnapshot, nextState?: RouterStateSnapshot): Observable<boolean> {
    // If component is  null then just bail out
    // (it looks like this could happen if maybe its still lazy loading when they click something else)
    if (component == null) {
      return of(true);
    }
    if (this.#forceNavigate(nextState)) {
      // If we're navigating to any "forceNavigate" the navigation HAS to happen. No questions asked...
      // Note: in general this is true, but I've noticed that if an error happens during destruction of the page (onPageLeave)
      // for example soemthing will get messed up and the error page itself will ask for confirmation to leave when you CLICK TO REFRESH
      // Forcing our global dirty check to false here to avoid that
      // PS - we have two "checkdirty" for the page, one is the original one written using redux, the other one, the global window object one
      // was added later to handle 3rd party components like filesaver that have nothing to do with our redux store.
      // I think maybe we should just use the global one and get rid of the redux one, but that's something to look into later. (#10256)
      window['safari_dirtyCheck'] = false;
      this.#checkNavigateAway(component, currentState, nextState);
      return of(true);
    }

    let shouldCheckDirty = false;
    // Let's see if we need to check for dirty (will be true if this was a cause of direct href click or if we explicitly set checkDirty
    // in dispatchNavigate call, as we do with Cancel button)
    // NOTE: Observables are inherently synchroneous unless they are written not to be , so the statement below is fine.
    // Subscribe will finish before we do the check further below
    this._store
      .select(this._routerReduxObject.default.selectors.checkDirty())
      .pipe(take(1))
      .subscribe(o => {
        shouldCheckDirty = o;
      });

    if (shouldCheckDirty && component.hasUnsavedChanges && component.hasUnsavedChanges()) {
      const leaveDialogId = this._uiManager.dispatchOpenDialog2Btn(
        null,
        'UNSAVED CHANGES',
        'Click OK to discard your changes, or click CANCEL to continue editing.',
        { name: 'OK', class: 'btn-outline-secondary btn-ghost-secondary', subtext: 'Discard Changes' },
        { name: 'CANCEL', class: 'btn-outline-secondary btn-ghost-secondary s_cancel', subtext: 'Continue Editing' }
      );
      return this._store.pipe(
        select(this._appDialogUiReduxObject.default.selectors.getDialogResponse),
        filter(o => o != null),
        filter(o => o.id === leaveDialogId),
        mergeMap(o => {
          if (o.buttonType === ConfirmationDialogButton.Ok) {
            this.#checkNavigateAway(component, currentState, nextState);
            return of(true);
          } else {
            return of(false);
          }
        }),
        take(1)
      );
    } else {
      this.#checkNavigateAway(component, currentState, nextState);
      return of(true);
    }
  }
}
